// First-party ("legacy account") sign-in: POST /api/v1/auth/login with the credentials the
// Django app used. The returned JWT is accepted by the gateway like an Authentik token and is
// kept in sessionStorage (per tab) under SESSION_KEY.
//
// The request/response shape lives ONLY in toLoginRequest / fromLoginResponse below.
import axios from 'axios';
import config from '../config';

export const SESSION_KEY = 'aidb.legacy.session';
const LOGIN_PATH = '/api/v1/auth/login';
/** Treat a token as expired slightly early so a request never leaves with a dying token. */
const SKEW_MS = 5000;

/** {identifier, password} -> request body. */
export const toLoginRequest = (identifier, password) => ({identifier: identifier.trim(), password});

/** Response body -> session `{accessToken, expiresAt (ms epoch), user}`; throws if unusable. */
export const fromLoginResponse = (data, now = Date.now()) => {
  const token = data && data.access_token;
  if (typeof token !== 'string' || !token) throw new Error('Malformed login response');
  const seconds = Number(data.expires_in);
  if (!Number.isFinite(seconds) || seconds <= 0) throw new Error('Malformed login response');
  return {accessToken: token, expiresAt: now + seconds * 1000, user: data.user || null};
};

const storage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const getSession = (now = Date.now()) => {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session || typeof session.accessToken !== 'string' || !(session.expiresAt - SKEW_MS > now)) {
      store.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    store.removeItem(SESSION_KEY);
    return null;
  }
};

export const saveSession = (session) => {
  const store = storage();
  if (store) store.setItem(SESSION_KEY, JSON.stringify(session));
};

export const clearSession = () => {
  const store = storage();
  if (store) store.removeItem(SESSION_KEY);
};

export const getLegacyToken = () => {
  const session = getSession();
  return session ? session.accessToken : null;
};

/** Signs in and stores the session. Rejects with a generic Error on any failure. */
export const loginWithPassword = async (identifier, password) => {
  try {
    const res = await axios.post(`${config.apiBaseUrl}${LOGIN_PATH}`, toLoginRequest(identifier, password), {
      headers: {Accept: 'application/json'},
    });
    const session = fromLoginResponse(res.data);
    saveSession(session);
    return session;
  } catch {
    // Never reveal whether the account exists or which field was wrong.
    throw new Error('Incorrect email, phone, username or password.');
  }
};

const LOGOUT_PATH = '/api/v1/auth/logout';
/** Bounds the sign-out wait: a hung revocation call must not keep the user on the page. */
const LOGOUT_TIMEOUT_MS = 5000;

const newIdempotencyKey = () => {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Ends the first-party session: revokes its token server-side (POST /api/v1/auth/logout, bearer
 * + Idempotency-Key), then ALWAYS clears the local session. Never rejects. A 401 (already revoked
 * or deactivated), a 503 (revocation store down) or a network error still leaves the browser
 * signed out. It uses its own axios call, not the shared client, so the client's 401 handler
 * cannot navigate in the middle of signing out.
 */
export const endFirstPartySession = async () => {
  const token = getLegacyToken();
  try {
    if (token) {
      await axios.post(`${config.apiBaseUrl}${LOGOUT_PATH}`, null, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'Idempotency-Key': newIdempotencyKey(),
        },
        timeout: LOGOUT_TIMEOUT_MS,
      });
    }
  } catch (err) {
    // Local sign-out still happens; the token may stay valid server-side until it expires.
    const status = err && err.response ? err.response.status : 'no response';
    console.warn(`Server sign-out was not confirmed (${status}); the local session was cleared.`);
  } finally {
    clearSession();
  }
};
